import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import SearchIcon from "@material-ui/icons/Search";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import { toast } from "react-toastify";

import ConfirmationModal from "../ConfirmationModal";
import RowActionsMenu from "../RowActionsMenu";
import TableRowSkeleton from "../TableRowSkeleton";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${fade(theme.palette.divider, 0.8)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  titleCell: {
    minWidth: 220,
  },
  titleText: {
    fontWeight: 600,
  },
  metaText: {
    opacity: 0.72,
  },
  descriptionCell: {
    maxWidth: 420,
  },
  videoChip: {
    fontWeight: 600,
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: "center",
  },
  dialogPaper: {
    borderRadius: 18,
  },
  dialogContent: {
    paddingTop: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
}));

const INITIAL_FORM = {
  id: undefined,
  title: "",
  description: "",
  video: "",
};

const resetHelpForm = () => ({
  id: undefined,
  title: "",
  description: "",
  video: "",
});

export default function HelpsManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useHelps();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({
    searchParam: "",
    videoStatus: "",
  });
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const loadHelps = useCallback(async () => {
    setLoading(true);
    try {
      const helpList = await list();
      setRecords(Array.isArray(helpList) ? helpList : []);
    } catch (error) {
      toast.error("Nao foi possivel carregar a lista de ajuda.");
    } finally {
      setLoading(false);
    }
  }, [list]);

  useEffect(() => {
    loadHelps();
  }, [loadHelps]);

  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      const haystack = `${item.title || ""} ${item.description || ""} ${item.video || ""}`
        .toLowerCase()
        .trim();
      const searchTerm = filters.searchParam.trim().toLowerCase();
      const hasVideo = Boolean(String(item.video || "").trim());

      const matchesSearch = !searchTerm || haystack.includes(searchTerm);
      const matchesVideo =
        !filters.videoStatus ||
        (filters.videoStatus === "withVideo" && hasVideo) ||
        (filters.videoStatus === "withoutVideo" && !hasVideo);

      return matchesSearch && matchesVideo;
    });
  }, [filters, records]);

  const summary = useMemo(() => {
    return filteredRecords.reduce(
      (accumulator, item) => {
        accumulator.visible += 1;
        if (String(item.video || "").trim()) {
          accumulator.withVideo += 1;
        } else {
          accumulator.withoutVideo += 1;
        }

        if (String(item.description || "").trim()) {
          accumulator.withDescription += 1;
        }

        return accumulator;
      },
      {
        visible: 0,
        withVideo: 0,
        withoutVideo: 0,
        withDescription: 0,
      }
    );
  }, [filteredRecords]);

  const summaryCards = useMemo(
    () => [
      { label: "Registros", value: summary.visible },
      { label: "Com video", value: summary.withVideo },
      { label: "Sem video", value: summary.withoutVideo },
      { label: "Com descricao", value: summary.withDescription },
    ],
    [summary]
  );

  const hasActiveFilters = useMemo(
    () => Boolean(filters.searchParam.trim() || filters.videoStatus),
    [filters]
  );

  const handleOpenCreateModal = () => {
    setFormData(resetHelpForm());
    setModalOpen(true);
  };

  const handleOpenEditModal = (record) => {
    setFormData({
      id: record.id,
      title: record.title || "",
      description: record.description || "",
      video: record.video || "",
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setModalOpen(false);
    setFormData(resetHelpForm());
  };

  const handleFormFieldChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;

    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      searchParam: "",
      videoStatus: "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        title: formData.title.trim(),
        description: formData.description.trim(),
        video: formData.video.trim(),
      };

      if (payload.id) {
        await update(payload);
      } else {
        await save(payload);
      }

      toast.success("Operacao realizada com sucesso.");
      setModalOpen(false);
      setFormData(resetHelpForm());
      await loadHelps();
    } catch (error) {
      toast.error(
        "Nao foi possivel salvar o registro. Verifique os campos e tente novamente."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!recordToDelete) return;

    setSaving(true);
    try {
      await remove(recordToDelete.id);
      toast.success("Operacao realizada com sucesso.");
      setRecordToDelete(null);
      setModalOpen(false);
      setFormData(resetHelpForm());
      await loadHelps();
    } catch (error) {
      toast.error("Nao foi possivel excluir o registro.");
    } finally {
      setSaving(false);
    }
  };

  const renderVideoChip = (value) => {
    const hasVideo = Boolean(String(value || "").trim());

    return (
      <Chip
        size="small"
        icon={<VideoLibraryIcon fontSize="small" />}
        label={hasVideo ? "Configurado" : "Sem video"}
        className={classes.videoChip}
        style={
          hasVideo
            ? { backgroundColor: "#dbeafe", color: "#1d4ed8" }
            : { backgroundColor: "#e5e7eb", color: "#374151" }
        }
      />
    );
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title="Excluir registro"
        open={Boolean(recordToDelete)}
        onClose={() => setRecordToDelete(null)}
        onConfirm={handleDelete}
      >
        Deseja realmente excluir este registro?
      </ConfirmationModal>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>{formData.id ? "Editar ajuda" : "Nova ajuda"}</DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Typography className={classes.sectionTitle}>Dados principais</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  label="Titulo"
                  value={formData.title}
                  onChange={handleFormFieldChange("title")}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={i18n.t("helps.settings.codeVideo")}
                  value={formData.video}
                  onChange={handleFormFieldChange("video")}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  size="small"
                  label={i18n.t("helps.settings.description")}
                  value={formData.description}
                  onChange={handleFormFieldChange("description")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {formData.id ? (
              <Button
                color="secondary"
                startIcon={<DeleteOutlineIcon />}
                onClick={() =>
                  setRecordToDelete({
                    id: formData.id,
                  })
                }
                disabled={saving}
              >
                Excluir
              </Button>
            ) : (
              <Box flex={1} />
            )}
            <Button onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={saving}>
              {saving ? "Salvando..." : i18n.t("helps.settings.save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("helps.title")}
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button
              variant="outlined"
              startIcon={<AutorenewIcon />}
              onClick={loadHelps}
              disabled={loading}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateModal}
            >
              Nova ajuda
            </Button>
          </Box>
        </Box>

        <div className={classes.summaryGrid}>
          {summaryCards.map((card) => (
            <Paper className={classes.summaryCard} elevation={0} key={card.label}>
              <Typography className={classes.summaryLabel}>{card.label}</Typography>
              <Typography className={classes.summaryValue}>{card.value}</Typography>
            </Paper>
          ))}
        </div>

        <Paper className={classes.filtersPaper} elevation={0}>
          <Box className={classes.filtersHeader}>
            <Typography variant="body1" className={classes.filtersTitle}>
              Filtros
            </Typography>
            <Box className={classes.filtersActions}>
              {hasActiveFilters ? <Chip size="small" label="Filtros ativos" /> : null}
              <Button onClick={handleClearFilters} disabled={!hasActiveFilters}>
                Limpar filtros
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Buscar por titulo, descricao ou video"
                value={filters.searchParam}
                onChange={handleFilterChange("searchParam")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                label="Video"
                value={filters.videoStatus}
                onChange={handleFilterChange("videoStatus")}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="withVideo">Com video</MenuItem>
                <MenuItem value="withoutVideo">Sem video</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.tablePaper} elevation={0}>
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Registros de ajuda
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <Chip size="small" label={`${filteredRecords.length} visiveis`} />
              <Chip size="small" label={`${summary.withVideo} com video`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Titulo</TableCell>
                  <TableCell>Descricao</TableCell>
                  <TableCell>Video</TableCell>
                  <TableCell align="right" className={classes.actionsCell}>
                    Acoes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  filteredRecords.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell className={classes.titleCell}>
                        <Typography className={classes.titleText}>{row.title || "-"}</Typography>
                        <Typography variant="body2" className={classes.metaText}>
                          ID #{row.id}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.descriptionCell}>
                        {row.description || "-"}
                      </TableCell>
                      <TableCell>{renderVideoChip(row.video)}</TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        <RowActionsMenu
                          buttonTitle="Acoes da ajuda"
                          actions={[
                            {
                              key: "edit",
                              label: "Editar ajuda",
                              icon: EditIcon,
                              onClick: () => handleOpenEditModal(row),
                            },
                            {
                              key: "delete",
                              label: "Excluir ajuda",
                              icon: DeleteOutlineIcon,
                              onClick: () => setRecordToDelete(row),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}

                {loading ? <TableRowSkeleton columns={4} /> : null}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filteredRecords.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6">
                {hasActiveFilters
                  ? "Nenhum registro encontrado para os filtros aplicados"
                  : "Nenhum registro de ajuda cadastrado"}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </div>
    </div>
  );
}
